<template>
  <div>
    <div
      v-for="(field, index) in localFields"
      :key="index"
      :style="{ marginLeft: field.indentation + 'px', padding: '0.5rem' }"
      :class="[
        'form-group',
        { 'notice-field': field.isNotice },
        { 'too-deep-field': field.tooDeep }
      ]"
    >
      <!-- Notice fields (informational only) -->
      <div v-if="field.isNotice" class="alert alert-info">
        <i class="fas fa-info-circle"></i>
        {{ field.value }}
      </div>
      
      <!-- Too deep fields (show simplified info) -->
      <div v-else-if="field.tooDeep" class="alert alert-warning">
        <strong>{{ field.key }}</strong>: Object (too deeply nested for field editor)
        <br><small>Switch to raw text mode to edit this nested object</small>
      </div>
      
      <!-- Regular fields -->
      <div v-else>
        <label style="font-weight: bold;">{{ field.key }}</label>
      <div class="row">
        <div class="col-xs-12 col-4 input-col">
          <label>Key</label>
          <input
            type="text"
            class="form-control"
            v-model="field.key"
            placeholder="Field Key"
            @input="updateParentJson"
            :disabled="readOnly"
          />
        </div>
        <div class="col-xs-12 col-4">
          <label>Type</label>
          <select class="form-control" v-model="field.type" @change="() => updateObject(index)" :disabled="readOnly">
            <option value="string">String</option>
            <option value="number">Number</option>
            <option value="boolean">Boolean</option>
            <option value="object">Nested Object</option>
            <option value="array">Array</option> <!-- Array Type Option -->
          </select>
        </div>
        <div v-if="field.type==='array'" class="col-xs-12 col-4" >
          <label>Array Element Type</label>
            <select class="form-control" v-model="field.arrayType" @change="updateObject(index)" :disabled="readOnly">
              <option value="string">String</option>
              <option value="number">Number</option>
              <option value="boolean">Boolean</option>
            </select>
        </div>
        <div class="col-xs-12 col-4" v-if="field.type !== 'object' && field.type !== 'array'">
          <label>Value</label>
          <div v-if="field.type === 'string'">
            <input
              type="text"
              class="form-control"
              v-model="field.value"
              placeholder="Field Value"
              @input="updateParentJson"
              :disabled="readOnly"
            />
          </div>
          <div v-if="field.type === 'number'">
            <input
              type="number"
              class="form-control"
              v-model.number="field.value"
              placeholder="Field Value"
              @input="updateParentJson"
              :disabled="readOnly"
            />
          </div>
          <div v-if="field.type === 'boolean'">
            <select class="form-control" v-model="field.value" @change="updateParentJson" :disabled="readOnly">
              <option :value="true">True</option>
              <option :value="false">False</option>
            </select>
          </div>
          </div>
        </div>
        <div v-if="field.type === 'array'" class="mt-4">
          
            <label class="d-block">Array Values</label>
            <div v-for="(item, itemIndex) in field.value" :key="itemIndex" class="p-2 mb-2">
                <input
                  v-if="field.arrayType === 'string'"
                  type="text"
                  class="form-control"
                  v-model="field.value[itemIndex]"
                  placeholder="Array Value"
                  @input="updateParentJson"
                  :disabled="readOnly"
                />
                <input
                  v-if="field.arrayType === 'number'"
                  type="number"
                  class="form-control"
                  v-model.number="field.value[itemIndex]"
                  placeholder="Array Value"
                  @input="updateParentJson"
                  :disabled="readOnly"
                />
                <select
                  v-if="field.arrayType === 'boolean'"
                  class="form-control"
                  v-model="field.value[itemIndex]"
                  @change="updateParentJson"
                  :disabled="readOnly"
                >
                  <option :value="true">True</option>
                  <option :value="false">False</option>
                </select>
              <button class="btn btn-danger" @click="removeArrayItem(index, itemIndex)" :disabled="readOnly">Remove Array Item</button>
            </div>
            <button class="btn btn-success" @click="addArrayItem(index)" :disabled="readOnly">Add Array Item</button>
      </div>
           <!-- Nested Object Handling -->
           <div v-if="field.type === 'object'" class="mt-4">
            <json-generator
              :fields="field.value"
              :indentation="field.indentation + 30"
              :readOnly="readOnly"
              @update-json="updateNestedJson(index, $event)"
            />
          </div>
      <button class="btn btn-danger mt-2" @click="removeField(index)" :disabled="readOnly">Remove Field</button>
      </div>
    </div>
    <button class="btn btn-success mt-3 mb-3" @click="addField" :disabled="readOnly">Add Field</button>
  </div>
</template>

<script>
export default {
  name: "JsonGenerator",
  props: {
    fields: {
      type: Array,
      default: () => [],
      required: true,
    },
    indentation: {
      type: Number,
      default: 0,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localFields: [],
    };
  },
  created() {
    this.localFields = this.fields.map((field) => ({ ...field }));
    
  },
  watch: {
    fields: {
      handler(newFields) {
        this.localFields = newFields.map((field) => ({ ...field }));
      },
      deep: true,
    },
  },
  methods: {
    addField() {
      this.localFields.push({ key: "", value: "", type: "string", arrayType: "string", indentation: this.indentation });
      this.updateParentJson();
    },
    removeField(index) {
      this.localFields.splice(index, 1);
      this.updateParentJson();
    },
    updateObject(index) {
      const tempField = this.localFields[index];
      if (tempField.type === "array") {
        tempField.value = [];
      } else if (tempField.type === "object") {
        tempField.value = [];
      } else if (tempField.type === "number") {
        tempField.value = 0;
      } else if (tempField.type === "boolean") {
        tempField.value = false;
      } else {
        tempField.value = "";
      }
      this.updateParentJson();
    },
    removeArrayItem(fieldIndex, itemIndex) {
      this.localFields[fieldIndex].value.splice(itemIndex, 1);
      this.updateParentJson();
    },
    addArrayItem(fieldIndex) {
      const itemType = this.localFields[fieldIndex].arrayType;
      let newValue = itemType === "object" ? [] : '';

      // Initialize the new value based on the specified type
      if (itemType === "number") {
        newValue = 0;
      } else if (itemType === "boolean") {
        newValue = false;
      }

      this.localFields[fieldIndex].value.push(newValue);
      this.updateParentJson();
    },
    updateNestedArrayObject(fieldIndex, itemIndex, updatedFields) {
      this.localFields[fieldIndex].value[itemIndex] = updatedFields;
      this.updateParentJson();
    },
    updateNestedJson(index, updatedFields) {
      this.localFields[index].value = updatedFields;
      this.updateParentJson();
    },
    updateParentJson() {
      this.$emit("update-json", this.localFields);
    },
  },
};
</script>

<style scoped>
.form-group {
  margin-bottom: 1rem;
  border-bottom: 2px dashed;
  padding-left: 30px;
  border-left: 2px dashed;
}
.form-control {
  background-color: #495057; /* Dark input fields */
  color: #ffffff; /* Light text color */
  border: 1px solid #6c757d; /* Darker border */
}
.form-control::placeholder {
  color: #ced4da; /* Lighter placeholder text */
}
.btn-link {
  color: #007bff;
}
.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
}
.btn-success {
  background-color: #28a745;
  border-color: #28a745;
}
.input-col {
  flex-direction: column;
  display: flex;
}

/* Notice and special field styles */
.notice-field {
  border: none !important;
  padding: 0.5rem !important;
  margin-bottom: 0.5rem !important;
}

.too-deep-field {
  border: none !important;
  padding: 0.5rem !important;
  margin-bottom: 0.5rem !important;
}

.alert {
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  border: 1px solid transparent;
  border-radius: 0.25rem;
}

.alert-info {
  color: #0c5460;
  background-color: #d1ecf1;
  border-color: #bee5eb;
}

.alert-warning {
  color: #856404;
  background-color: #fff3cd;
  border-color: #ffeaa7;
}
</style>
